// Pestaña "Casos" de la pantalla del asistente: asuntos que esperan algo (un reintegro que no llegó,
// una denuncia sin enlazar, una moderación…). Lista con filtros y panel de detalle con sus correos,
// sus acciones y los botones para cerrarlo o reintentarlo.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, RefreshCw, X, Loader2, RotateCcw, CheckCircle2, Ban, FolderOpen } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Can } from '../../lib/rbac';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Pagination } from '../../components/shared/Pagination';
import { usePagination } from '../../hooks/usePagination';
import { EmailDetailDrawer } from './EmailDetailDrawer';
import { ActionsMini } from './ActionsMini';
import {
  loadMeta,
  asPage,
  optionsOf,
  caseKindLabel,
  caseStatusLabel,
  caseStatusTone,
  typeLabel,
  emailStateWords,
  statusTone,
  fmtDateTime,
  fmtValue,
  fmtShortDateTime,
  caseDataKeyLabel,
  CASE_KIND_LABEL,
  CASE_STATUS_LABEL,
  type AssistantMeta,
  type CaseRow,
  type CaseEmailRow,
  type ActionRow,
} from './shared';

type Row = CaseRow & Record<string, unknown>;

interface CaseDetailData {
  case: CaseRow;
  emails: CaseEmailRow[];
  actions: ActionRow[];
}

interface RetryCaseResult {
  retried: number;
  results: Array<{ email_id: number; status: string; handler_summary: string | null }>;
}

const MAX_PER_PAGE = 200;
const OPEN_AND_WAITING = 'open,waiting';

const inputCls =
  'px-2 py-1 text-xs bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded text-[var(--color-text)]';
const btnGhost =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-tertiary)] disabled:opacity-40';
const btnPrimary =
  'inline-flex items-center gap-1 px-3 py-1 text-xs rounded bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-40';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs py-1">
      <span className="w-32 shrink-0 text-[var(--color-text-muted)]">{label}</span>
      <span className="min-w-0 break-words text-[var(--color-text)]">{children}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">{title}</div>
      {children}
    </div>
  );
}

const isClosed = (s: string) => s === 'resolved' || s === 'dismissed';

// ── Datos del caso con render propio (seguimiento de reclamo) ────────────────

interface CaseNoteItem {
  at: string;
  by: string;
  text: string;
}

const isNoteList = (v: unknown): v is CaseNoteItem[] =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every((n) => n && typeof n === 'object' && typeof (n as CaseNoteItem).text === 'string');

const isLinkList = (v: unknown): v is Array<{ entity_type: string; entity_id: string | number }> =>
  Array.isArray(v) &&
  v.length > 0 &&
  v.every((l) => l && typeof l === 'object' && 'entity_type' in l && 'entity_id' in l);

const LINK_WORD: Record<string, string> = { claim: 'reclamo', order: 'venta' };

/** Plazo: fecha sola (aaaa-mm-dd) vence al final de ese día; con hora, en ese momento. */
function deadlineInfo(v: string): { text: string; past: boolean } | null {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.exec(v);
  if (dateOnly) {
    const [y, m, d] = v.split('-').map(Number);
    const end = new Date(y, m - 1, d, 23, 59, 59);
    return {
      text: `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${String(y).slice(2)}`,
      past: end.getTime() < Date.now(),
    };
  }
  const t = new Date(v);
  if (Number.isNaN(t.getTime())) return null;
  return { text: fmtDateTime(v), past: t.getTime() < Date.now() };
}

function CaseDataValue({ k, v }: { k: string; v: unknown }) {
  if (k === 'notes' && isNoteList(v)) {
    const sorted = [...v].sort((a, b) => String(a.at ?? '').localeCompare(String(b.at ?? '')));
    return (
      <ul className="space-y-2">
        {sorted.map((n, i) => (
          <li key={i} className="border-l-2 border-[var(--color-border)] pl-2">
            <div className="text-[11px] text-[var(--color-text-muted)]">
              {fmtShortDateTime(n.at)}
              {n.by ? ` · ${n.by}` : ''}
            </div>
            <div className="whitespace-pre-line">{n.text}</div>
          </li>
        ))}
      </ul>
    );
  }
  if (k === 'deadline' && typeof v === 'string' && v) {
    const info = deadlineInfo(v);
    if (info)
      return (
        <span className={info.past ? 'font-semibold text-[var(--color-error)]' : ''}>
          {info.text}
          {info.past ? ' (vencido)' : ''}
        </span>
      );
  }
  if (k === 'decision' && typeof v === 'string' && v.trim()) {
    return (
      <span className="block rounded border border-[var(--color-warning)] bg-[var(--color-warning-light)] px-2 py-1 font-semibold whitespace-pre-line">
        {v}
      </span>
    );
  }
  if (k === 'links' && isLinkList(v)) {
    return (
      <span className="flex flex-wrap gap-1">
        {v.map((l, i) => (
          <span
            key={i}
            className="rounded-full border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] px-2 py-0.5 text-[11px]"
          >
            {LINK_WORD[l.entity_type] ?? l.entity_type} {String(l.entity_id)}
          </span>
        ))}
      </span>
    );
  }
  if ((k === 'next_step' || k === 'summary') && typeof v === 'string' && v) {
    return <span className="whitespace-pre-line">{v}</span>;
  }
  return <>{fmtValue(v)}</>;
}

function CaseDetail({
  caseId,
  sellerName,
  onClose,
  onChanged,
  onOpenEmail,
  reloadKey,
}: {
  caseId: number;
  /** Cambia cuando algo afuera del panel tocó el caso (p. ej. un reintento en el correo abierto encima). */
  reloadKey: number;
  sellerName: (id: number) => string;
  onClose: () => void;
  onChanged: () => void;
  onOpenEmail: (emailId: number) => void;
}) {
  const [detail, setDetail] = useState<CaseDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);
  // Cierre en dos clics: primero se elige (resolver / descartar), después se confirma con nota opcional.
  const [pending, setPending] = useState<'resolved' | 'dismissed' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const reqSeq = useRef(0);
  // Caso que se está mostrando: las respuestas de cierre/reintento de otro caso se descartan.
  const caseIdRef = useRef(caseId);
  caseIdRef.current = caseId;

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<CaseDetailData>(`/assistant/cases/${caseId}`);
      if (seq !== reqSeq.current) return;
      if (res.success && res.data?.case) {
        setDetail({
          case: res.data.case,
          emails: res.data.emails ?? [],
          actions: res.data.actions ?? [],
        });
      } else {
        setDetail(null);
        setError(res.error || 'No se pudo abrir el caso.');
      }
    } catch {
      if (seq === reqSeq.current) {
        setDetail(null);
        setError('No se pudo abrir el caso.');
      }
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    setPending(null);
    setNote('');
    setMsg(null);
    setShowRaw(false);
    void load();
  }, [load]);

  // Recarga sin borrar el mensaje en pantalla cuando el caso cambió desde afuera del panel.
  const firstReload = useRef(true);
  useEffect(() => {
    if (firstReload.current) {
      firstReload.current = false;
      return;
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const close = async () => {
    if (!pending || !detail) return;
    const id = detail.case.id;
    setBusy(true);
    setMsg(null);
    try {
      const body: { status: 'resolved' | 'dismissed'; note?: string } = { status: pending };
      if (note.trim()) body.note = note.trim();
      const res = await api.patch<CaseRow>(`/assistant/cases/${id}`, body);
      if (caseIdRef.current !== id) {
        if (res.success) onChanged();
        return;
      }
      if (res.success) {
        setMsg({ kind: 'ok', text: pending === 'resolved' ? 'Caso marcado como resuelto.' : 'Caso descartado.' });
        setPending(null);
        setNote('');
        onChanged(); // recarga lista, contador y este panel
      } else {
        setMsg({ kind: 'err', text: res.error || 'No se pudo cambiar el estado del caso.' });
      }
    } catch {
      if (caseIdRef.current === id) setMsg({ kind: 'err', text: 'No se pudo cambiar el estado del caso.' });
    } finally {
      setBusy(false);
    }
  };

  const retry = async () => {
    if (!detail) return;
    const id = detail.case.id;
    setBusy(true);
    setMsg(null);
    try {
      const res = await api.post<RetryCaseResult>(`/assistant/cases/${id}/retry`, {});
      if (caseIdRef.current !== id) {
        if (res.success) onChanged();
        return;
      }
      if (res.success && res.data) {
        const n = Number(res.data.retried) || 0;
        const parts = (res.data.results ?? []).map(
          (r) => `correo #${r.email_id}: ${emailStateWords(r.status)}${r.handler_summary ? ` — ${r.handler_summary}` : ''}`
        );
        const head =
          n === 0
            ? 'No había correos para reintentar (solo se reintentan los que están esperando o fallaron).'
            : `Se reintentaron ${n} ${n === 1 ? 'correo' : 'correos'}.`;
        setMsg({ kind: 'ok', text: [head, ...parts].join('\n') });
        onChanged(); // recarga lista, contador y este panel
      } else {
        setMsg({ kind: 'err', text: res.error || 'No se pudo reintentar el caso.' });
      }
    } catch {
      if (caseIdRef.current === id) setMsg({ kind: 'err', text: 'No se pudo reintentar el caso.' });
    } finally {
      setBusy(false);
    }
  };

  const c = detail?.case;
  const dataEntries = c?.data && typeof c.data === 'object' ? Object.entries(c.data) : [];

  return (
    <div className="h-full flex flex-col bg-[var(--color-bg)] lg:rounded-lg lg:border lg:border-[var(--color-border)]">
      <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen size={18} className="text-[var(--color-primary)] shrink-0" />
          <div className="text-sm font-bold text-[var(--color-text)] truncate" title={c?.title ?? ''}>
            {c ? c.title || caseKindLabel(c.kind) : 'Caso'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar el detalle del caso"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {loading && !detail ? (
          <div className="flex items-center justify-center py-12 text-[var(--color-text-secondary)]">
            <Loader2 size={18} className="animate-spin mr-2" /> Cargando…
          </div>
        ) : error || !c || !detail ? (
          <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
            {error || 'No se pudo abrir el caso.'}
          </div>
        ) : (
          <>
            {msg && (
              <div
                className={`rounded-lg p-3 text-sm whitespace-pre-line ${
                  msg.kind === 'ok'
                    ? 'bg-[var(--color-success-bg,rgba(34,197,94,0.1))] text-[var(--color-success)]'
                    : 'bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]'
                }`}
              >
                {msg.text}
              </div>
            )}

            <Section title="Datos del caso">
              <Field label="Empresa">{sellerName(c.seller_id)}</Field>
              <Field label="Tipo">{caseKindLabel(c.kind)}</Field>
              <Field label="Estado">
                <StatusBadge status={caseStatusTone(c.status)} label={caseStatusLabel(c.status)} size="sm" />
              </Field>
              <Field label="Abierto el">{fmtDateTime(c.created_at)}</Field>
              <Field label="Última actividad">{fmtDateTime(c.last_email_at ?? c.updated_at)}</Field>
              {!isClosed(c.status) && <Field label="Próximo chequeo">{fmtDateTime(c.next_check_at)}</Field>}
              <Field label="Intentos">{c.attempts ?? 0}</Field>
              {isClosed(c.status) && (
                <>
                  <Field label="Cerrado el">{fmtDateTime(c.resolved_at)}</Field>
                  <Field label="Cerrado por">{c.resolved_by || '—'}</Field>
                  <Field label="Nota">{c.resolution_note || '—'}</Field>
                </>
              )}
              {dataEntries.map(([k, v]) => (
                <Field key={k} label={caseDataKeyLabel(k)}>
                  <CaseDataValue k={k} v={v} />
                </Field>
              ))}
              {dataEntries.length > 0 && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setShowRaw((s) => !s)}
                    className="text-xs text-[var(--color-primary)] hover:underline"
                  >
                    {showRaw ? 'Ocultar datos completos' : 'Ver datos completos'}
                  </button>
                  {showRaw && (
                    <pre className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-2 text-[11px] text-[var(--color-text)] whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                      {JSON.stringify(c.data, null, 2)}
                    </pre>
                  )}
                </div>
              )}
            </Section>

            <Can cap="assistant.actions">
              <Section title="Acciones sobre el caso">
                {pending ? (
                  <div className="space-y-2 rounded-lg border border-[var(--color-border)] p-3">
                    <div className="text-xs text-[var(--color-text)]">
                      {pending === 'resolved'
                        ? '¿Marcar este caso como resuelto? El asistente deja de seguirlo.'
                        : '¿Descartar este caso? El asistente deja de seguirlo sin darlo por resuelto.'}
                    </div>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      maxLength={500}
                      placeholder="Nota (opcional)"
                      aria-label="Nota"
                      className={`${inputCls} w-full`}
                    />
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => void close()} disabled={busy} className={btnPrimary}>
                        {busy && <Loader2 size={12} className="animate-spin" />}
                        {pending === 'resolved' ? 'Sí, marcar resuelto' : 'Sí, descartar'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPending(null);
                          setNote('');
                        }}
                        disabled={busy}
                        className={btnGhost}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setPending('resolved')}
                      disabled={busy || isClosed(c.status)}
                      title={isClosed(c.status) ? 'El caso ya está cerrado' : undefined}
                      className={btnGhost}
                    >
                      <CheckCircle2 size={12} /> Marcar resuelto
                    </button>
                    <button
                      type="button"
                      onClick={() => setPending('dismissed')}
                      disabled={busy || isClosed(c.status)}
                      title={isClosed(c.status) ? 'El caso ya está cerrado' : undefined}
                      className={btnGhost}
                    >
                      <Ban size={12} /> Descartar
                    </button>
                    <button
                      type="button"
                      onClick={() => void retry()}
                      disabled={busy || isClosed(c.status)}
                      title={
                        isClosed(c.status)
                          ? 'El caso ya está cerrado'
                          : 'Vuelve a procesar los correos del caso que están esperando o fallaron'
                      }
                      className={btnGhost}
                    >
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Reintentar
                    </button>
                  </div>
                )}
              </Section>
            </Can>

            <Section title={`Correos enlazados (${detail.emails.length})`}>
              {detail.emails.length === 0 ? (
                <div className="text-xs text-[var(--color-text-muted)]">Sin correos enlazados.</div>
              ) : (
                <ul className="space-y-1">
                  {detail.emails.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        onClick={() => onOpenEmail(e.id)}
                        className="w-full text-left rounded px-2 py-1.5 hover:bg-[var(--color-surface-hover)] space-y-0.5"
                      >
                        <div className="flex items-center gap-2 text-xs">
                          <span className="min-w-0 flex-1 truncate text-[var(--color-text)]" title={e.subject ?? ''}>
                            {e.subject || '(sin asunto)'}
                          </span>
                          <StatusBadge status={statusTone(e.status)} label={emailStateWords(e.status)} size="sm" />
                        </div>
                        <div className="text-[11px] text-[var(--color-text-muted)]">
                          {fmtDateTime(e.received_at)} · {typeLabel(e.type)}
                        </div>
                        {e.handler_summary && (
                          <div className="text-[11px] text-[var(--color-text-secondary)] break-words">
                            {e.handler_summary}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Acciones registradas">
              <ActionsMini actions={detail.actions} />
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

export default function CasesTab({
  focusCaseId,
  focusNonce,
  onFocusConsumed,
  onChanged,
}: {
  /** Se llama tras abrir el caso pedido, para que no se vuelva a abrir solo al regresar a la pestaña. */
  onFocusConsumed?: () => void;
  /** Caso a abrir al entrar (desde "Ver caso" en un correo o el registro). */
  focusCaseId?: number | null;
  /** Cambia cada vez que se pide abrir un caso (aunque sea el mismo). */
  focusNonce?: number;
  /** Se avisa al cambiar un caso (para refrescar el contador de la pestaña). */
  onChanged?: () => void;
}) {
  const sellers = useUIStore((s) => s.sellers);
  const pag = usePagination('assistant-cases');

  const [meta, setMeta] = useState<AssistantMeta | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState(OPEN_AND_WAITING);
  const [kind, setKind] = useState('');
  const [seller, setSeller] = useState('');
  const [selected, setSelected] = useState<number | null>(focusCaseId ?? null);
  const [openEmail, setOpenEmail] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reqSeq = useRef(0);

  useEffect(() => {
    if (focusCaseId) {
      setSelected(focusCaseId);
      onFocusConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCaseId, focusNonce]);

  // Recarga del panel del caso (se sube cuando algo lo tocó desde afuera).
  const [caseReload, setCaseReload] = useState(0);

  useEffect(() => {
    let alive = true;
    void loadMeta().then((m) => {
      if (alive && m) setMeta(m);
    });
    return () => {
      alive = false;
    };
  }, []);

  const sellerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const s of sellers) map.set(s.id, s.nickname);
    return (id: number) => map.get(id) ?? String(id);
  }, [sellers]);

  useEffect(() => {
    if (pag.perPage > MAX_PER_PAGE) pag.setPerPage(MAX_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.perPage]);

  const loadList = useCallback(async () => {
    if (pag.perPage > MAX_PER_PAGE) return;
    const seq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(pag.perPage),
        offset: String((pag.page - 1) * pag.perPage),
      });
      if (status) params.set('status', status);
      if (kind) params.set('kind', kind);
      if (seller) params.set('seller_id', seller);
      const res = await api.get<unknown>(`/assistant/cases?${params}`);
      if (seq !== reqSeq.current) return;
      if (res.success) {
        const page = asPage<Row>(res.data);
        setRows(page.rows);
        pag.setTotal(page.total);
      } else {
        setRows([]);
        pag.setTotal(0);
        setError(res.error || 'No se pudieron cargar los casos.');
      }
    } catch {
      if (seq === reqSeq.current) setError('No se pudieron cargar los casos.');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pag.page, pag.perPage, status, kind, seller]);

  useEffect(() => {
    void loadList();
  }, [loadList, reloadKey]);

  useEffect(() => {
    pag.resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, kind, seller]);

  const changed = () => {
    setReloadKey((k) => k + 1);
    setCaseReload((k) => k + 1);
    onChanged?.();
  };

  // Escape cierra el panel del caso (si hay un correo abierto encima, no: ese va primero).
  useEffect(() => {
    if (selected === null || openEmail !== null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelected(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, openEmail]);

  // En pantalla angosta el fondo oscuro cierra el panel; en ancha el panel queda fijo al costado
  // y solo se cierra con su botón (o Escape).
  const closeOnBackdrop = () => {
    if (typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches) return;
    setSelected(null);
  };

  const kinds = optionsOf(meta?.case_kinds, CASE_KIND_LABEL);
  const statuses = optionsOf(meta?.case_statuses, CASE_STATUS_LABEL);

  const columns = [
    {
      key: 'seller_id',
      label: 'Empresa',
      render: (r: Row) => <span className="text-xs whitespace-nowrap">{sellerName(r.seller_id)}</span>,
    },
    {
      key: 'kind',
      label: 'Tipo',
      render: (r: Row) => <span className="text-xs whitespace-nowrap">{caseKindLabel(r.kind)}</span>,
    },
    {
      key: 'title',
      label: 'Título',
      render: (r: Row) => (
        <span className="block max-w-[340px] truncate" title={r.title}>
          {r.title || '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (r: Row) => (
        <StatusBadge status={caseStatusTone(r.status)} label={caseStatusLabel(r.status)} size="sm" />
      ),
    },
    {
      key: 'emails_count',
      label: 'Correos',
      render: (r: Row) => <span className="text-xs">{Number(r.emails_count) || 0}</span>,
    },
    {
      key: 'last_email_at',
      label: 'Última actividad',
      render: (r: Row) => (
        <span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
          {fmtDateTime(r.last_email_at ?? r.updated_at)}
        </span>
      ),
    },
    {
      key: 'next_check_at',
      label: 'Próximo chequeo',
      render: (r: Row) => (
        <span className="text-xs text-[var(--color-text-secondary)] whitespace-nowrap">
          {isClosed(r.status) ? '—' : fmtDateTime(r.next_check_at)}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-[var(--color-text-secondary)]">
        Asuntos que el asistente sigue porque esperan algo: un reintegro que no llegó, una denuncia que todavía no
        aparece, una moderación sin resolver. Hacé clic en una fila para ver el detalle.
      </p>

      <div className="flex flex-wrap items-center gap-2 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
        <Filter className="w-4 h-4 text-[var(--color-text-secondary)]" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls} aria-label="Estado">
          <option value={OPEN_AND_WAITING}>Abiertos y esperando</option>
          {statuses.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
          <option value="">Todos los estados</option>
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls} aria-label="Tipo">
          <option value="">Todos los tipos</option>
          {kinds.map((k) => (
            <option key={k.key} value={k.key}>
              {k.label}
            </option>
          ))}
        </select>
        {sellers.length > 1 && (
          <select value={seller} onChange={(e) => setSeller(e.target.value)} className={inputCls} aria-label="Empresa">
            <option value="">Todas las empresas</option>
            {sellers.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.nickname}
              </option>
            ))}
          </select>
        )}
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          title="Actualizar"
          aria-label="Actualizar"
          className="p-1 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="rounded-lg p-3 text-sm bg-[var(--color-error-bg,rgba(239,68,68,0.1))] text-[var(--color-error)]">
          {error}
        </div>
      )}

      <div className={selected !== null ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_28rem] lg:gap-4 lg:items-start' : ''}>
        <div className="space-y-4 min-w-0">
          <DataTable<Row>
            columns={columns}
            data={rows}
            loading={loading}
            emptyMessage="No hay casos con estos filtros."
            onRowClick={(r) => setSelected(r.id)}
          />
          <Pagination
            page={pag.page}
            perPage={pag.perPage}
            total={pag.total}
            totalPages={pag.totalPages}
            onPageChange={pag.setPage}
            onPerPageChange={pag.setPerPage}
          />
        </div>

        {selected !== null && (
          // En pantalla ancha: panel a la derecha. En angosta: panel lateral superpuesto.
          <div
            className="fixed inset-0 z-50 flex justify-end bg-black/50 lg:static lg:z-auto lg:block lg:bg-transparent"
            onClick={closeOnBackdrop}
          >
            <div
              className="w-full max-w-lg h-full lg:max-w-none lg:h-[calc(100vh-12rem)] lg:sticky lg:top-4"
              onClick={(e) => e.stopPropagation()}
            >
              <CaseDetail
                caseId={selected}
                sellerName={sellerName}
                onClose={() => setSelected(null)}
                onChanged={changed}
                onOpenEmail={setOpenEmail}
                reloadKey={caseReload}
              />
            </div>
          </div>
        )}
      </div>

      {openEmail !== null && (
        <EmailDetailDrawer
          emailId={openEmail}
          sellerName={sellerName}
          onClose={() => {
            setOpenEmail(null);
            // Al volver al caso, traerlo de nuevo (el correo pudo cambiar su estado o el del caso).
            setCaseReload((k) => k + 1);
          }}
          onChanged={changed}
          onOpenCase={(id) => {
            setOpenEmail(null);
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}
